import { HeartIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { receiptDocumentPath } from '../../../api/receipt.api';
import { FIELD_LABEL } from '../../../components/detail/detail-classes';
import { DownloadLabel } from '../../../components/detail/DownloadLabel';
import { Button } from '../../../components/ui/button';
import { DONATION_TONE } from '../../../constants/donation.const';
import { useDownloadInFlight } from '../../../hooks/useDownloadInFlight';
import type {
  DonationCurrencyTotal,
  DonationYearSummary
} from '../../../types/donation.types';
import {
  formatDonationPeriod,
  pluralize
} from '../../../utils/donation-format';
import { formatCurrency } from '../../../utils/fiat-currency.util';

interface DonationYearSummaryCardProps {
  summary: DonationYearSummary;
  onDownloadSummary: () => void;
}

/** One figure per currency, "RM 86.20 + S$ 5.00" when a year has two. */
const perCurrency = (
  totals: DonationCurrencyTotal[],
  pick: (total: DonationCurrencyTotal) => number
) => totals.map((t) => formatCurrency(pick(t), t.currency)).join(' + ');

/** The year's total, split by tax status, with the summary PDF. */
export function DonationYearSummaryCard({
  summary,
  onDownloadSummary
}: DonationYearSummaryCardProps) {
  const { year, totals } = summary;
  const hasTaxDeductible = totals.some((t) => t.tax_deductible_total > 0);
  const preparing = useDownloadInFlight(receiptDocumentPath.summary(year));

  return (
    <section
      aria-label={`Your ${year} total`}
      className={clsx('rounded-3xl border p-5 shadow-sm', DONATION_TONE.card)}
    >
      <p
        className={clsx(
          'flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest',
          DONATION_TONE.text
        )}
      >
        <HeartIcon className="h-4 w-4" aria-hidden="true" />
        Your {year} total
      </p>
      <p className={clsx('mt-2 text-3xl font-bold', DONATION_TONE.text)}>
        {perCurrency(totals, (t) => t.total)}
      </p>
      <p className="mt-1 text-sm text-gray-600">
        {pluralize(summary.donation_count, 'donation', 'donations')} to{' '}
        {pluralize(summary.charity_count, 'charity', 'charities')} ·{' '}
        {formatDonationPeriod(summary.period_from, summary.period_to)}
      </p>

      <dl className="mt-4 grid grid-cols-2 gap-4 border-y border-[#5CB891]/30 py-4">
        <div>
          <dt className={FIELD_LABEL}>Tax-deductible</dt>
          <dd className="mt-1 text-base font-semibold text-gray-900">
            {perCurrency(totals, (t) => t.tax_deductible_total)}
          </dd>
        </div>
        <div>
          <dt className={FIELD_LABEL}>Not deductible</dt>
          <dd className="mt-1 text-base font-semibold text-gray-900">
            {perCurrency(totals, (t) => t.not_deductible_total)}
          </dd>
        </div>
      </dl>

      <Button
        type="button"
        onClick={onDownloadSummary}
        disabled={preparing}
        aria-busy={preparing}
        className={clsx(
          'mt-4 h-12 w-full rounded-xl text-sm font-semibold',
          DONATION_TONE.filledButton
        )}
      >
        <DownloadLabel pending={preparing}>
          Download {year} summary (PDF)
        </DownloadLabel>
      </Button>
      <p className="mt-3 text-xs text-gray-600">
        {hasTaxDeductible
          ? 'For your records. Official tax receipts are issued per donation by each charity, below.'
          : 'For your records. Each donation also has its own receipt, below.'}
      </p>
    </section>
  );
}
